import clsx from 'clsx'
import SepaCreationTab from 'components/billing/sepa/SepaCreationTab'
import SepaOverviewTab from 'components/billing/sepa/SepaOverviewTab'
import Page from 'components/ui/Page'
import { useAppSelector } from 'hooks/hooks'
import { useSearchParams } from 'react-router-dom'

type TabId = 'overview' | 'create'

const TABS: { id: TabId; label: string; hint: string }[] = [
    { id: 'overview', label: 'Overview', hint: 'All SEPA payouts, grouped per batch' },
    { id: 'create', label: 'SEPA creation', hint: 'Create a batch for open credit notes' },
]

/**
 * Credit note SEPAs page (TRUS-8359): payouts to providers for credit notes
 * (negative invoices), done via a SEPA XML file that Finance uploads to the
 * bank. The overview groups per generated file (batch) with re-download +
 * bulk fulfil; the creation tab lists the credit notes that still need a
 * payout.
 */
const SepaPage = () => {
    // Tab lives in the URL so views are shareable and survive a refresh.
    const [searchParams, setSearchParams] = useSearchParams()
    const activeTab: TabId = searchParams.get('tab') === 'create' ? 'create' : 'overview'
    const { countries } = useAppSelector((state) => state.countries)

    const setTab = (tab: TabId) => {
        setSearchParams(
            (prev) => {
                prev.set('tab', tab)
                return prev
            },
            { replace: true },
        )
    }

    return (
        <Page title="Credit note SEPAs">
            <div className="mx-auto max-w-screen-2xl px-1 py-6">
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold text-dark">Credit note SEPAs</h1>
                    {countries.map((country) => (
                        <span
                            key={country}
                            className="rounded-full bg-primary-50 px-2.5 py-0.5 text-md font-medium text-primary-700">
                            {country}
                        </span>
                    ))}
                </div>
                <p className="mt-1 text-md text-neutral-500">
                    Credit notes are paid out to providers via a SEPA file that is uploaded to the
                    bank. Every generated file is stored as a batch, so it can be re-downloaded here
                    at any time.
                </p>

                <div className="mt-6 flex gap-1 border-b border-neutral-200">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setTab(tab.id)}
                            className={clsx(
                                '-mb-px border-b-2 px-4 py-3 text-left transition',
                                activeTab === tab.id
                                    ? 'border-primary'
                                    : 'border-transparent hover:bg-neutral-50',
                            )}>
                            <span
                                className={clsx(
                                    'block text-md font-semibold',
                                    activeTab === tab.id ? 'text-primary' : 'text-neutral-700',
                                )}>
                                {tab.label}
                            </span>
                            <span className="block text-sm text-neutral-400">{tab.hint}</span>
                        </button>
                    ))}
                </div>

                <div className="mt-6">
                    {activeTab === 'overview' ? <SepaOverviewTab /> : <SepaCreationTab />}
                </div>
            </div>
        </Page>
    )
}

export default SepaPage
